import React from 'react';
import { useNavigation } from '../context/NavigationContext';
import { legalConfig, isFieldPlaceholder } from '../../config/legalConfig';
import { FileText, Shield, Calendar, ArrowLeft, ExternalLink } from 'lucide-react';

export const TermsPage: React.FC = () => {
  const { navigate } = useNavigation();

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        {/* Back Link */}
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-0.5" />
          <span>Ana Sayfaya Dön</span>
        </button>

        {/* Document Header */}
        <div className="border-b border-slate-800 pb-8 mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-blue-500/10 text-blue-400 border border-blue-500/20 mb-4">
            <FileText className="w-3.5 h-3.5" />
            <span>Sözleşme Metni</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-extrabold text-white tracking-tight leading-tight">
            ADIOSOFT KULLANIM KOŞULLARI
          </h1>
          <div className="mt-4 flex flex-wrap items-center gap-6 text-xs text-slate-400">
            <span className="flex items-center gap-1.5">
              <Shield className="w-3.5 h-3.5 text-blue-400" />
              <span>Hukuki İşletmeci: <strong className="text-slate-200">{legalConfig.companyName}</strong></span>
            </span>
            <span className="flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5 text-blue-400" />
              <span>Yürürlük Tarihi: <strong className="text-slate-200">{legalConfig.effectiveDate}</strong></span>
            </span>
            <span className="text-slate-500">Sürüm: {legalConfig.termsVersion}</span>
          </div>
          <p className="mt-4 text-sm text-slate-300 leading-relaxed">
            {legalConfig.relationshipStatement} Lütfen platformumuzu kullanmadan veya herhangi bir dijital lisans/abonelik paketi satın almadan önce bu Kullanım Koşulları’nı dikkatlice okuyunuz.
          </p>
        </div>

        {/* Document Content - 31 Structured Sections */}
        <div className="prose prose-invert prose-slate max-w-none text-sm leading-relaxed space-y-8 text-slate-300">
          {/* 1. Taraflar */}
          <section className="bg-slate-900/50 p-6 rounded-2xl border border-slate-800/80">
            <h2 className="text-base font-bold text-white mb-3">1. Taraflar</h2>
            <p>
              İşbu Kullanım Koşulları Sözleşmesi (&quot;Sözleşme&quot;); bir tarafta Adiosoft dijital platformunun işletmecisi olan ve merkezi{' '}
              <strong className="text-white">{legalConfig.registeredOffice}</strong> adresinde mukim{' '}
              <strong className="text-white">{legalConfig.companyName}</strong> (&quot;Sağlayıcı&quot; veya &quot;Şirket&quot;) ile diğer tarafta{' '}
              <span className="text-blue-400">{legalConfig.platformUrl}</span> internet sitesi ve platforma üye olan, web sitesi oluşturan veya dijital paket satın alan gerçek veya tüzel kişi (&quot;Kullanıcı&quot; veya &quot;Müşteri&quot;) arasında akdedilmiştir.
            </p>
          </section>

          {/* 2. Tanımlar */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">2. Tanımlar</h2>
            <ul className="list-disc pl-5 space-y-1.5 text-slate-400">
              <li><strong className="text-slate-200">Platform:</strong> Adiosoft markası altında Getpad Inc. tarafından işletilen web sitesi, editör ve SaaS yazılım altyapısıdır.</li>
              <li><strong className="text-slate-200">Web Sitesi Oluşturucu (Website Builder):</strong> Kullanıcıların kodlama bilmeksizin sektörel web siteleri oluşturmasını sağlayan görsel yazılım motorudur.</li>
              <li><strong className="text-slate-200">AI Local Visibility (Yerel Görünürlük):</strong> İşletmelerin yerel yapay zekâ arama ve harita kaynaklarındaki görünürlüğünü test eden ve teknik öneri sunan analiz modülüdür.</li>
              <li><strong className="text-slate-200">Lisans ve Altyapı İşlemleri:</strong> Kullanıcının mevcut harici web sitesini Adiosoft bulut altyapısına bağlayarak 1 yıllık (365 gün) süreyle lisanslama ve güvenli altyapı desteği almasını sağlayan teknik operasyondur.</li>
              <li><strong className="text-slate-200">Dijital Lisans / Abonelik:</strong> Kullanıcıya satın aldığı paket süresiyle sınırlı olarak platformu kullanma, yayınlama ve içerik yönetme imkânı tanıyan süreli kullanım hakkıdır. Mülkiyet devri niteliği taşımaz.</li>
            </ul>
          </section>

          {/* 3. Sözleşmenin Konusu */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">3. Sözleşmenin Konusu</h2>
            <p>
              İşbu Sözleşme&apos;nin konusu; Kullanıcı&apos;nın Platform üzerinden sunulan dijital hizmetlerden, yazılım araçlarından ve ek modüllerden faydalanmasına ilişkin hak ve yükümlülüklerin belirlenmesidir.
            </p>
          </section>

          {/* 4. Üyelik & 5. Hesap Güvenliği */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">4. Üyelik ve 5. Hesap Güvenliği</h2>
            <p>
              Platform&apos;a üye olurken verilen bilgilerin doğru, eksiksiz ve güncel olması zorunludur. Kullanıcı, hesap kimlik bilgilerini ve şifresini gizli tutmakla bizzat yükümlüdür. Hesabın yetkisiz kullanımı şüphesinde Sağlayıcı&apos;ya derhal bildirimde bulunulmalıdır.
            </p>
          </section>

          {/* 6. Platformun Kullanımı */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">6. Platformun Kullanımı</h2>
            <p>
              Platform yalnızca yasalara, genel ahlaka ve ticari dürüstlük kurallarına uygun amaçlarla kullanılabilir. Sisteme zarar verici yazılımlar yüklenemez, tersine mühendislik yapılamaz ve altyapı güvenliğini tehlikeye sokacak eylemlerde bulunulamaz.
            </p>
          </section>

          {/* 7. Web Sitesi Oluşturma Hizmeti & Kural: 1 Hesap = 1 Aktif Website */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">7. Web Sitesi Oluşturma Hizmeti ve Tekil Lisans İlkesi</h2>
            <p>
              Adiosoft standart website paketleri <strong className="text-white">1 Hesap = 1 Aktif Website Lisansı = 1 Özel Alan Adı</strong> mimarisiyle çalışır. Satın alınan paket tek bir web sitesinin barındırılmasını ve yönetilmesini kapsar. Aynı hesap üzerinden ikinci bir site yayına alınmak istendiğinde ek paket veya yeni lisans temin edilmelidir.
            </p>
          </section>

          {/* 8. AI Hizmetleri ve Doğrulama */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">8. Yapay Zekâ (AI) Hizmetleri ve Sonuçların Niteliği</h2>
            <p>
              Platform bünyesinde sunulan AI metin, tasarım veya yerel arama analizi modülleri optimizasyon ve öneri amaçlıdır. Kullanıcı, AI tarafından üretilen içeriklerin doğruluğunu, sektörel mevzuata uygunluğunu ve üçüncü kişi haklarını ihlal etmediğini bizzat kontrol etmekle yükümlüdür. <strong className="text-white">AI Local Visibility hizmeti belirli bir arama motorunda veya yapay zekâ modelinde (Google, ChatGPT, Gemini vb.) kesin 1. sıra ya da tavsiye edilme garantisi vermez.</strong>
            </p>
          </section>

          {/* 9. Lisans ve Altyapı Entegrasyon Hizmeti */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">9. Lisans ve Altyapı Entegrasyon Hizmeti</h2>
            <p>
              Harici site lisanslama ve altyapı bağlantı hizmeti; kullanıcının web sitesinin Adiosoft bulut altyapısı üzerinden 365 gün süreyle kesintisiz doğrulanmasını ve yayınlanmasını kapsar. Kurulum işlemleri yönetim ekibimiz tarafından arka planda tamamlanır. Yıllık süre sonunda yenilenmeyen lisanslar otomatik olarak askıya alınır.
            </p>
          </section>

          {/* 10. Alan Adı İşlemleri */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">10. Alan Adı (Custom Domain) İşlemleri</h2>
            <p>
              Kullanıcıların sitelerine bağladıkları özel alan adları Kullanıcı&apos;nın kendi mülkiyetindedir. Adiosoft, alan adının DNS yönlendirmesi ve güvenli bulut altyapısına bağlanması için teknik koordinasyonu sağlar; alan adı tescil kuruluşunun kuralları veya Kullanıcı&apos;nın DNS hatalarından sorumlu tutulamaz.
            </p>
          </section>

          {/* 11. Lisans ve Kullanım Süresi (CRITICAL CLAUSE) */}
          <section className="bg-blue-950/20 p-5 rounded-2xl border border-blue-500/30 space-y-2">
            <h2 className="text-base font-bold text-white flex items-center gap-2">
              <Calendar className="w-4 h-4 text-blue-400" />
              <span>11. Lisans ve Kullanım Süresi (Süreli Dijital Lisans)</span>
            </h2>
            <p className="text-slate-200">
              Satın alınan tüm Adiosoft dijital paketleri, <strong className="text-white">satın alma sırasında seçilen lisans ve abonelik dönemi ile kesin olarak sınırlıdır</strong> (örneğin 1 yıllık paket için başlangıç tarihinden itibaren 12 takvim ayı).
            </p>
            <p className="text-slate-300">
              Bu hak mülkiyet devri niteliğinde olmayıp süreli bir yazılım ve platform lisansı sağlar. Lisans süresi sona erdiğinde ve süresinde yenilenmediğinde; website yayın hakkı pasife alınabilir, alan adı yönlendirmesi durdurulabilir. Yenileme yapıldığında hizmet derhal yeniden aktif hale getirilir.
            </p>
          </section>

          {/* 12. Ücretlendirme & 13. Ödeme */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">12. Ücretlendirme ve 13. Ödeme Koşulları</h2>
            <p>
              Hizmet bedelleri Türk Lirası cinsinden ve KDV dahil olarak checkout ekranında açıkça belirtilir. Ödemeler yetkili ödeme altyapısı sağlayıcısı aracılığıyla 256-bit SSL güvenlik protokolü altında kredi kartı veya banka kartı ile tahsil edilir.
            </p>
          </section>

          {/* 14. Cayma ve İade (CRITICAL TURKISH CONSUMER COMPLIANCE) */}
          <section className="bg-slate-900/60 p-5 rounded-2xl border border-slate-800 space-y-2">
            <h2 className="text-base font-bold text-white">14. Cayma Hakkı, İstisnaları ve İade Politikası</h2>
            <p>
              İade ve cayma hakkı, 6502 sayılı Tüketicinin Korunması Hakkında Kanun ve Mesafeli Sözleşmeler Yönetmeliği hükümleri çerçevesinde uygulanır.
            </p>
            <p>
              Mesafeli Sözleşmeler Yönetmeliği&apos;nin 15. maddesinin (ğ) bendi uyarınca; <strong className="text-white">elektronik ortamda anında ifa edilen hizmetler veya tüketicinin açık onayı ile cayma süresi dolmadan ifasına başlanan hizmetler bakımından kanuni cayma hakkı istisnası uygulanır.</strong>
            </p>
            <p className="text-xs text-slate-400">
              Kullanıcı sipariş esnasında hizmetin derhal başlatılmasını talep ettiğinde ve dijital lisans/web sitesi anında oluşturulduğunda bu istisna devreye girer. Bununla birlikte, 6502 sayılı Kanun kapsamındaki ayıplı hizmete ilişkin tüketici hakları ve kanuni zorunlu iade halleri saklıdır.
            </p>
          </section>

          {/* 15. Abonelik Sona Ermesi & 16. İçerik Sorumluluğu */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">15. Abonelik Sona Ermesi ve 16. Kullanıcının İçerik Sorumluluğu</h2>
            <p>
              Kullanıcı tarafından web sitesine yüklenen tüm metin, fotoğraf, logo, video ve ticari markaların telif ve kişilik haklarına uygunluğu münhasıran Kullanıcı&apos;nın sorumluluğundadır. Sağlayıcı, Kullanıcı içeriklerinin hukuka aykırılığı nedeniyle üçüncü kişilere karşı sorumlu tutulamaz.
            </p>
          </section>

          {/* 17. Fikri Mülkiyet Hakları */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">17. Fikri Mülkiyet Hakları</h2>
            <p>
              Adiosoft platformunun kaynak kodu, veri tabanı mimarisi, tescilli markası, görsel arayüz tasarımı ve algoritmaları Getpad Inc.&apos;e aittir. Kullanıcı&apos;ya yalnızca süreli kullanım lisansı verilmiş olup bu materyaller kopyalanamaz veya yetkisiz çoğaltılamaz.
            </p>
          </section>

          {/* 18-24. Diğer Hukuki Hükümler */}
          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">18. Üçüncü Taraf Entegrasyonları ve 20. Hizmet Kesintileri</h2>
            <p>
              Platform, güvenli bulut sağlayıcıları ve telekomünikasyon altyapıları üzerinden çalışır. Planlı bakım çalışmaları önceden duyurulmak kaydıyla gerçekleştirilir. Mücbir sebepler ve küresel internet omurgası kesintileri nedeniyle oluşabilecek geçici erişim aksaklıklarında makul süre içerisinde hizmetin yeniden sağlanması hedeflenir.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">21. Hesap Askıya Alma ve Fesih</h2>
            <p>
              Hukuka aykırı içerik barındıran, kötü niyetli siber saldırı girişiminde bulunan veya sözleşme yükümlülüklerini ihlal eden hesaplar Sağlayıcı tarafından tek taraflı olarak geçici veya kalıcı olarak askıya alınabilir.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">25. Kişisel Veriler ve Gizlilik</h2>
            <p>
              Kişisel veriler, 6698 sayılı Kişisel Verilerin Korunması Kanunu (&quot;KVKK&quot;) ve ilgili mevzuata uygun olarak işlenir. Detaylı bilgi için <button onClick={() => navigate('/kvkk-aydinlatma-metni')} className="text-blue-400 hover:underline">KVKK Aydınlatma Metni</button> ve <button onClick={() => navigate('/gizlilik-politikasi')} className="text-blue-400 hover:underline">Gizlilik Politikası</button> incelenmelidir.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">26. Elektronik Kayıt ve Delil Sözleşmesi</h2>
            <p>
              Taraflar arasında doğabilecek uyuşmazlıklarda Sağlayıcı&apos;nın sunucu logları, veri tabanı kayıtları, elektronik imza ve checkout kabul kayıtları HMK m. 193 uyarınca kesin delil niteliğindedir.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">28. Uygulanacak Hukuk ve 29. Uyuşmazlık Çözümü</h2>
            <p>
              İşbu Sözleşme&apos;nin yorumlanmasında ve uygulanmasında Türkiye Cumhuriyeti hukuku uygulanır. Doğabilecek uyuşmazlıklarda her yıl Ticaret Bakanlığı&apos;nca ilan edilen parasal sınırlar dahilinde Tüketici Hakem Heyetleri ile Tüketici Mahkemeleri yetkilidir.
            </p>
          </section>

          <section className="space-y-2">
            <h2 className="text-base font-bold text-white">30. Değişiklikler ve 31. Yürürlük</h2>
            <p>
              Sağlayıcı, mevzuat değişiklikleri veya platform geliştirmeleri gerektirdiğinde işbu Sözleşme&apos;yi güncelleme hakkını saklı tutar. Güncel sürüm Platform&apos;da yayınlandığı andan itibaren yürürlüğe girer.
            </p>
          </section>
        </div>

        {/* Company Contact Footer */}
        <div className="mt-12 p-6 rounded-2xl bg-slate-900 border border-slate-800 text-xs text-slate-400 space-y-2">
          <p className="font-semibold text-slate-200">Resmi Şirket ve İletişim Bilgileri:</p>
          <p>İşletmeci: <strong className="text-white">{legalConfig.companyName}</strong></p>
          <p>Adres: {legalConfig.registeredOffice}</p>
          <p>E-Posta: {legalConfig.email}</p>
          <p>Telefon: {legalConfig.phone}</p>
          {isFieldPlaceholder(legalConfig.registeredOffice) && (
            <p className="text-[11px] text-amber-400/80 pt-2 border-t border-slate-800">
              * Kurumsal şirket adresi ve resmi kayıt bilgileri Getpad Inc. yönetim organları tarafından tamamlanma aşamasındadır.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
export default TermsPage;
